/**
 * Creator report ("Analyze", docs/SPEC.md §19): windows, KPI cards, sorts and chart data, defined ONCE so the
 * dashboard, share pages, exports and MCP never disagree. Pure; times are epoch ms.
 * Scores are the server's (rule 5): this module only aggregates stored values, it never re-scores.
 */
import { median } from './score';

export type ReportWindow = { kind: 'all' } | { kind: 'latest'; n: number } | { kind: 'days'; days: number };

export const REPORT_LATEST_CHOICES = [50, 100, 200, 500, 1000] as const;
export const REPORT_DAY_CHOICES = [7, 30, 90] as const;
/**
 * Every reel we have (decision 84, owner 2026-10-21): the creator page, share links made without a window, the report
 * API and MCP default to all reels; "Latest N" and "Last N days" stay as choices.
 */
export const DEFAULT_WINDOW: ReportWindow = { kind: 'all' };
/**
 * Most reels one report reads (newest first; decision 84, was 1,000): "all reels" covers creators up to 5,000 reels. One
 * query, cached in KV 10 minutes; the app lists 60 at a time ("Load more"). A layout bound, not a plan limit.
 */
export const REPORT_MAX_REELS = 5_000;

/** `all` | `latest:200` | `days:30` → window (anything else → null). */
export function parseWindow(v: string | null | undefined): ReportWindow | null {
  if (v === 'all') return { kind: 'all' };
  const m = /^(latest|days):(\d{1,4})$/.exec(v ?? '');
  if (!m) return null;
  const n = Number(m[2]);
  if (m[1] === 'latest') return (REPORT_LATEST_CHOICES as readonly number[]).includes(n) ? { kind: 'latest', n } : null;
  return (REPORT_DAY_CHOICES as readonly number[]).includes(n) ? { kind: 'days', days: n } : null;
}
export const windowKey = (w: ReportWindow) => (w.kind === 'all' ? 'all' : w.kind === 'latest' ? `latest:${w.n}` : `days:${w.days}`);
/** "All reels" (with the count when known: "All reels (484)"), "Latest 200 reels", "Last 30 days". */
export const windowLabel = (w: ReportWindow, total?: number | null) =>
  w.kind === 'all' ? `All reels${typeof total === 'number' ? ` (${total})` : ''}` : w.kind === 'latest' ? `Latest ${w.n} reels` : `Last ${w.days} days`;

export interface ReportReel {
  pk: string;
  shortcode: string;
  postedAt: number | null;
  durationS: number | null;
  caption: string | null;
  hashtags: string[];
  views: number | null;
  likes: number | null;
  likesHidden: boolean;
  comments: number | null;
  shares: number | null;
  isPinned: boolean;
  isPaidPartnership: boolean;
  /**
   * Owned by another account; this creator is a coauthor. Not part of the KPIs or the baseline; its Wise Score is
   * against THIS creator's usual views and its virality is null (decision 71).
   */
  isCollab: boolean;
  outlierScore: number | null;
  viralityScore: number | null;
  hasTranscript: boolean;
  /**
   * This org's own just-uploaded reel, not corroborated yet (decision 75): shown only in this org's signed-in report,
   * never scored, never in the KPIs, the baseline or a public share.
   */
  verifying?: boolean;
  thumbUrl: string | null;
  audio: { id: string; title: string | null; artist: string | null; type: 'original' | 'licensed' | null } | null;
  /** Saved video (R2), when kept; videos are deleted after the retention setting. */
  videoUrl?: string | null;
  /** Decision 80: a transcription has something to read (audio track, or a video with sound). Absent from older rows. */
  transcribable?: boolean;
  /** Decision 80: the saved video is the low-quality picture-only file (it plays without sound). */
  videoSilent?: boolean;
  videoSavedAt?: number | null;
  /** Spoken hook, when transcribed (hidden for Arabic script / blocked transcripts). */
  hook?: string | null;
  hookScript?: string | null;
  /** Decision 91: the owner and the co-authors (handles when known), and the party the card names (collabLabel). */
  ownerPk?: string;
  ownerHandle?: string | null;
  collaborators?: Array<{ igPk: string; handle: string | null }>;
  collabWith?: { igPk: string; handle: string | null } | null;
}

/**
 * The card's collab chip (decision 91): "Collab with @handle" naming the other party (the owner of a collab on this
 * creator's page; the co-author on a collab-only profile's page), "Collab" when we don't know its handle, null when the
 * reel isn't shown as a collab.
 */
export function collabLabel(r: { isCollab?: boolean; collabWith?: { handle: string | null } | null }): string | null {
  if (!r.isCollab && !r.collabWith) return null;
  return r.collabWith?.handle ? `Collab with @${r.collabWith.handle}` : 'Collab';
}

const sum = (xs: number[]) => xs.reduce((a, b) => a + b, 0);
const nums = (xs: Array<number | null>) => xs.filter((x): x is number => typeof x === 'number' && Number.isFinite(x));

/**
 * Engagement rate of one reel: (likes + comments) / views. Null when likes are hidden (decision 71): counting them as 0
 * would make the reel look like it had almost no engagement, so it is left out of medians and sorts last.
 */
export function engagement(r: Pick<ReportReel, 'likes' | 'comments' | 'views'> & { likesHidden?: boolean }): number | null {
  if (!r.views || r.views <= 0 || r.likesHidden) return null;
  return ((r.likes ?? 0) + (r.comments ?? 0)) / r.views;
}

/** Median engagement of the creator's own reels with visible likes (the "High engagement" preset compares with it). */
export function medianOwnEngagement(reels: ReportReel[]): number | null {
  return median(nums(reels.filter((r) => !r.isCollab).map(engagement)));
}

/**
 * Reels in the window, newest first. `latest` takes the creator's newest N OWN reels (pinned reels by their post date)
 * plus the collabs posted in that span (decision 71: "Latest 200" means 200 of their own reels, collabs listed besides).
 */
export function applyWindow(reels: ReportReel[], w: ReportWindow, now: number): ReportReel[] {
  const sorted = [...reels].sort((a, b) => (b.postedAt ?? 0) - (a.postedAt ?? 0) || (b.pk > a.pk ? 1 : -1));
  if (w.kind === 'all') return sorted;
  if (w.kind === 'latest') {
    // Stops right after the Nth own reel: collabs newer than it are in, older ones are not (all of them when the
    // creator has fewer than N own reels).
    const out: ReportReel[] = [];
    let own = 0;
    for (const r of sorted) {
      if (own === w.n) break;
      out.push(r);
      if (!r.isCollab) own++;
    }
    return out;
  }
  const from = now - w.days * 86_400_000;
  return sorted.filter((r) => r.postedAt !== null && r.postedAt >= from);
}

/** Own reels and collabs in a window (the header says "N reels analysed" over own reels, decision 71). */
export function reelCounts(windowed: ReportReel[]): { own: number; collabs: number } {
  const collabs = windowed.filter((r) => r.isCollab).length;
  return { own: windowed.length - collabs, collabs };
}

/**
 * How many of the window's own reels carry each detail (decision 71): the length, hashtag and audio cards say
 * "based on N of M reels" when a scan didn't return that detail for every reel (Instagram's grid has no caption or
 * length; the post-scan details add them for the top reels only).
 */
export function reportCoverage(windowed: ReportReel[]): { reels: number; withLength: number; withCaption: number; withAudio: number } {
  const own = windowed.filter((r) => !r.isCollab);
  return {
    reels: own.length,
    withLength: own.filter((r) => r.durationS !== null).length,
    withCaption: own.filter((r) => r.caption !== null).length,
    withAudio: own.filter((r) => r.audio !== null).length,
  };
}

/** "based on 12 of 200 reels" when `n` < `total`, else null (every reel counted). */
export function basedOn(n: number, total: number): string | null {
  return n < total ? `based on ${n} of ${total} reels` : null;
}

export interface ReportKpis {
  reels: number;
  totalViews: number;
  /** The creator's outlier baseline (§9.1), not recomputed here. Usual LIKES when `scoreBasis` is 'likes'. */
  baselineViews: number | null;
  /** What the baseline counts: 'views', or 'likes' for a creator whose reels show no views (§9.1); null = no baseline. */
  scoreBasis: 'views' | 'likes' | null;
  medianViews: number | null;
  avgViews: number | null;
  totalLikes: number;
  likesHiddenOn: number;
  totalComments: number;
  medianEngagement: number | null;
  medianDurationS: number | null;
  viewToFollower: number | null;
  outlierHitRate: number | null;
  postsPerWeek: number | null;
  topAudio: { id: string; title: string | null; artist: string | null; count: number } | null;
}

/** One-line definitions shown as tooltips everywhere a KPI appears. */
export const KPI_DEFINITIONS: Record<keyof ReportKpis, string> = {
  reels: "The creator's own reels in the selected window (collabs owned by others are listed and scored against this creator's usual views, but not counted).",
  totalViews: 'Sum of views of those reels.',
  baselineViews: "The creator's usual views: median of all their qualifying reels we have (their own reels, not pinned, older than 72 h). The Wise Score divides by this. For a creator whose reels show no views it is their usual likes.",
  scoreBasis: "What the creator's Wise Scores compare: views, or likes when none of their reels show views.",
  medianViews: 'Median views in the selected window.',
  avgViews: 'Total views ÷ reels. A few huge hits pull this up, so the median is the better guide.',
  totalLikes: 'Sum of likes where likes are visible.',
  likesHiddenOn: 'Reels where the creator hid likes.',
  totalComments: 'Sum of comments.',
  medianEngagement: 'Median of (likes + comments) ÷ views per reel. Reels with hidden likes are left out.',
  medianDurationS: 'Median reel length.',
  viewToFollower: 'Median views ÷ followers.',
  outlierHitRate: 'Share of scored reels with a Wise Score at the Hot threshold (3× by default) or above.',
  postsPerWeek: 'Reels per week between the oldest and newest reel in the window (or over the chosen days).',
  topAudio: 'The audio used most often by reels at the Hot threshold or above.',
};

/**
 * The Usual views tooltip with the creator's own count (decision 83): "median of all 412 of their qualifying reels we
 * have"; with a latest-N window (`scores.window` > 0) "median of their 30 most recent qualifying reels". Falls back to
 * the generic definition when the count is unknown.
 */
export function baselineDefinition(n: number | null | undefined, window = 0): string {
  const PHRASE = 'median of all their qualifying reels we have';
  const known = typeof n === 'number' && Number.isFinite(n) && n > 0;
  if (window > 0) return KPI_DEFINITIONS.baselineViews.replace(PHRASE, `median of their ${(known ? n : window).toLocaleString('en-US')} most recent qualifying reels`);
  if (!known) return KPI_DEFINITIONS.baselineViews;
  return KPI_DEFINITIONS.baselineViews.replace(PHRASE, `median of all ${n.toLocaleString('en-US')} of their qualifying reels we have`);
}

export function computeKpis(
  windowed: ReportReel[],
  opts: { followers: number | null; baselineViews: number | null; outlierThreshold: number; window: ReportWindow; scoreBasis?: string | null },
): ReportKpis {
  // Own reels only; this org's verifying reels (decision 75) never count in the KPIs.
  const own = windowed.filter((r) => !r.isCollab && !r.verifying);
  const views = nums(own.map((r) => r.views));
  const medianViews = median(views);
  const scored = own.filter((r) => r.outlierScore !== null);
  const outliers = scored.filter((r) => r.outlierScore! >= opts.outlierThreshold);

  const dated = nums(own.map((r) => r.postedAt));
  let postsPerWeek: number | null = null;
  if (opts.window.kind === 'days') postsPerWeek = own.length / (opts.window.days / 7);
  else if (dated.length >= 2) {
    const spanDays = Math.max(1, (Math.max(...dated) - Math.min(...dated)) / 86_400_000);
    postsPerWeek = (dated.length - 1) / (spanDays / 7);
  }

  const audioCounts = new Map<string, { id: string; title: string | null; artist: string | null; count: number }>();
  for (const r of outliers) {
    if (!r.audio) continue;
    const a = audioCounts.get(r.audio.id) ?? { id: r.audio.id, title: r.audio.title, artist: r.audio.artist, count: 0 };
    a.count++;
    audioCounts.set(r.audio.id, a);
  }
  const topAudio = [...audioCounts.values()].sort((a, b) => b.count - a.count)[0] ?? null;

  return {
    reels: own.length,
    totalViews: sum(views),
    baselineViews: opts.baselineViews,
    scoreBasis: opts.scoreBasis === 'views' || opts.scoreBasis === 'likes' ? opts.scoreBasis : null,
    medianViews,
    avgViews: views.length ? sum(views) / views.length : null,
    totalLikes: sum(nums(own.filter((r) => !r.likesHidden).map((r) => r.likes))),
    likesHiddenOn: own.filter((r) => r.likesHidden).length,
    totalComments: sum(nums(own.map((r) => r.comments))),
    medianEngagement: medianOwnEngagement(own),
    medianDurationS: median(nums(own.map((r) => r.durationS))),
    viewToFollower: medianViews !== null && opts.followers ? medianViews / opts.followers : null,
    outlierHitRate: scored.length ? outliers.length / scored.length : null,
    postsPerWeek: postsPerWeek === null ? null : Math.round(postsPerWeek * 10) / 10,
    topAudio: topAudio && topAudio.count >= 2 ? topAudio : null,
  };
}

export const REEL_SORTS = ['outlier', 'views', 'likes', 'comments', 'engagement', 'virality', 'newest'] as const;
export type ReelSort = (typeof REEL_SORTS)[number];
export const SORT_LABELS: Record<ReelSort, string> = {
  outlier: 'Wise Score', views: 'Views', likes: 'Likes', comments: 'Comments', engagement: 'Engagement', virality: 'Virality', newest: 'Newest',
};

/** Sorted copy; missing values sort last. */
export function sortReels(reels: ReportReel[], by: ReelSort): ReportReel[] {
  const key = (r: ReportReel): number | null =>
    by === 'outlier' ? r.outlierScore
      : by === 'views' ? r.views
      : by === 'likes' ? (r.likesHidden ? null : r.likes)
      : by === 'comments' ? r.comments
      : by === 'engagement' ? engagement(r)
      : by === 'virality' ? r.viralityScore
      : r.postedAt;
  return [...reels].sort((a, b) => {
    const ka = key(a);
    const kb = key(b);
    if (ka === null && kb === null) return 0;
    if (ka === null) return 1;
    if (kb === null) return -1;
    return kb - ka;
  });
}

// ---- charts -------------------------------------------------------------------------------------------

export interface ScatterPoint { pk: string; x: number; y: number; outlier: boolean; collab: boolean }

/** Outlier scatter: posted date × views (the UI draws y on a log scale), plus baseline and threshold lines. */
export function scatter(windowed: ReportReel[], baselineViews: number | null, outlierThreshold: number) {
  const points: ScatterPoint[] = windowed
    .filter((r) => r.postedAt !== null && r.views !== null && r.views > 0)
    .map((r) => ({ pk: r.pk, x: r.postedAt!, y: r.views!, outlier: (r.outlierScore ?? 0) >= outlierThreshold, collab: r.isCollab }));
  return { points, baseline: baselineViews, threshold: baselineViews !== null ? baselineViews * outlierThreshold : null };
}

export const DURATION_BUCKETS = [
  { label: '0–15 s', min: 0, max: 15 },
  { label: '15–30 s', min: 15, max: 30 },
  { label: '30–60 s', min: 30, max: 60 },
  { label: '60–90 s', min: 60, max: 90 },
  { label: '90 s+', min: 90, max: Infinity },
] as const;

/** Duration vs performance: reels and median outlier score per length bucket. */
export function durationBuckets(windowed: ReportReel[]) {
  return DURATION_BUCKETS.map((b) => {
    const inB = windowed.filter((r) => !r.isCollab && r.durationS !== null && r.durationS >= b.min && r.durationS < b.max);
    return { label: b.label, reels: inB.length, medianOutlier: median(nums(inB.map((r) => r.outlierScore))) };
  });
}

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
export const HEATMAP_DAYS = WEEKDAYS;

/** Day (0 = Monday) and hour (0–23) of a time in a time zone. */
export function dayHourIn(ms: number, timeZone: string): { day: number; hour: number } {
  const parts = new Intl.DateTimeFormat('en-US', { timeZone, weekday: 'short', hour: 'numeric', hourCycle: 'h23' }).formatToParts(new Date(ms));
  const wd = parts.find((p) => p.type === 'weekday')?.value ?? 'Mon';
  const hour = Number(parts.find((p) => p.type === 'hour')?.value ?? 0) % 24;
  return { day: Math.max(0, WEEKDAYS.indexOf(wd)), hour };
}

/**
 * Posting pattern heatmap, 7 × 24 cells in the viewer's time zone.
 *   mode 'count' → how often they post · mode 'works' → median outlier score per slot (null when < 2 reels).
 */
export function heatmap(windowed: ReportReel[], timeZone: string, mode: 'count' | 'works'): Array<Array<number | null>> {
  const cells: number[][][] = Array.from({ length: 7 }, () => Array.from({ length: 24 }, () => []));
  for (const r of windowed) {
    if (r.isCollab || r.postedAt === null) continue;
    const { day, hour } = dayHourIn(r.postedAt, timeZone);
    cells[day]![hour]!.push(r.outlierScore ?? NaN);
  }
  return cells.map((row) => row.map((c) => {
    if (mode === 'count') return c.length;
    const scored = c.filter((x) => Number.isFinite(x));
    return scored.length >= 2 ? median(scored) : null;
  }));
}

/** Top hashtags by median outlier score (min 2 reels each). */
export function hashtagLeaders(windowed: ReportReel[], limit = 10) {
  const by = new Map<string, number[]>();
  for (const r of windowed) {
    if (r.isCollab) continue;
    for (const h of new Set(r.hashtags.map((x) => x.toLowerCase()))) {
      const arr = by.get(h) ?? [];
      if (r.outlierScore !== null) arr.push(r.outlierScore);
      by.set(h, arr);
    }
  }
  return [...by.entries()]
    .filter(([, s]) => s.length >= 2)
    .map(([tag, s]) => ({ tag, reels: s.length, medianOutlier: median(s)! }))
    .sort((a, b) => b.medianOutlier - a.medianOutlier || b.reels - a.reels)
    .slice(0, limit);
}

/** Audio: original vs licensed share and the top audios with their outlier rate. */
export function audioLeaders(windowed: ReportReel[], outlierThreshold: number, limit = 8) {
  const own = windowed.filter((r) => !r.isCollab && r.audio);
  const original = own.filter((r) => r.audio!.type === 'original').length;
  const licensed = own.filter((r) => r.audio!.type === 'licensed').length;
  const by = new Map<string, { id: string; title: string | null; artist: string | null; reels: number; outliers: number }>();
  for (const r of own) {
    const a = by.get(r.audio!.id) ?? { id: r.audio!.id, title: r.audio!.title, artist: r.audio!.artist, reels: 0, outliers: 0 };
    a.reels++;
    if ((r.outlierScore ?? 0) >= outlierThreshold) a.outliers++;
    by.set(r.audio!.id, a);
  }
  const top = [...by.values()].sort((a, b) => b.outliers - a.outliers || b.reels - a.reels).slice(0, limit);
  return { original, licensed, top };
}
